/**
 * Quality — Merged Search Tester + Evaluation Runs
 *
 * Two sub-tabs within one page:
 * - Search Tester: real RAG retrieval diagnostics (W2-G-09 — backed by
 *   POST /api/v1/knowledge/search-test, returns dense + sparse + RRF-fused).
 * - Evaluation Runs: RAGAS benchmark results — still mock-driven; that
 *   migration is W2-G-08, not in G-09 scope.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Search, FlaskConical, FileText, Loader2,
  Sparkles, SlidersHorizontal, Play, Clock, CheckCircle,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/library/quality')({
  component: QualityPage,
});

// ─── Search Tester Types — backed by POST /api/v1/knowledge/search-test ─────
//
// W2-G-09 — Search Tester now hits the real RAG retrieval pipeline. The
// backend returns three parallel result lists (dense + sparse + RRF-fused)
// so admins can debug retrieval quality directly from the UI.

interface DenseHit {
  chunkId: string;
  score: number;
  text: string;
  documentId: string | null;
  // #235 — backend now resolves the title alongside the id so the
  // result card can show "Audit L1 Markdown Test" instead of a
  // bare UUID prefix.
  documentTitle: string | null;
  page: number | null;
}

interface SparseHit {
  chunkId: string;
  score: number;
  text: string;
  documentId: string | null;
  documentTitle: string | null;
}

interface FusedHit {
  chunkId: string;
  denseRank: number | null;
  sparseRank: number | null;
  fusedScore: number;
  text: string;
  documentId: string | null;
  documentTitle: string | null;
  source: 'dense' | 'sparse' | 'both';
}

interface SearchTestResponse {
  query: string;
  topK: number;
  durationMs: number;
  dense: DenseHit[];
  sparse: SparseHit[];
  fused: FusedHit[];
  // #680 — Confidence classification from the backend so the UI can
  // render an explicit warning banner for nonsense / low-relevance
  // queries instead of letting dense nearest-neighbor chunks look
  // like a confident retrieval. New optional fields so deploys can
  // roll forward independently of the backend release.
  confidence?: 'high' | 'medium' | 'low' | 'none';
  warning?: string;
}

// ─── W2-G-08: EvalRun wire format lives in EvaluationSection (below) ────────

// ─── Page Component ─────────────────────────────────────────────────────────

function QualityPage() {
  // R2 P2 #2 — the Search Tester sub-tab is server-side gated to
  // power_user (POST /knowledge/search-test returns 403 to non-power
  // users). The TAB itself used to render for everyone, so a `member`
  // would click Search and get a silent toast error. Now we gate the
  // tab on the same role; non-power users see only Evaluation Runs and
  // are landed there by default. Cleaner UX, no failed network round-
  // trip, no toast noise.
  // B1-T3: was `useAuth().hasRole('power_user')`. The server gates
  // POST /knowledge/search-test on `delete:library.documents` (knowledge.ts —
  // the key B1-T2 gave the deleted ladder's power_user rung), so the tab and
  // the endpoint now ask the same question.
  const canSearchTest = useHasPermission('delete', 'library.documents');

  const [activeTab, setActiveTab] = useState<'search' | 'evaluation'>(
    canSearchTest ? 'search' : 'evaluation'
  );

  // Defensive: if the role flips after mount (unlikely but possible on
  // a token refresh), don't strand a `member` on the now-hidden tab.
  useEffect(() => {
    if (!canSearchTest && activeTab === 'search') {
      setActiveTab('evaluation');
    }
  }, [canSearchTest, activeTab]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Quality</h2>
          <p className="text-sm text-muted-foreground">Test search retrieval and evaluate RAG pipeline accuracy.</p>
        </div>
      </div>

      {/* C-A11Y cluster (issue #692 LIB-A11Y Quality critical):
          Previously the TabsTrigger panels were rendered as siblings
          (conditional `{activeTab === 'search' && ...}`) instead of as
          TabsContent children. Radix sets aria-controls on each trigger
          pointing to a TabsContent id; without TabsContent the id never
          materialises and axe flags it as `aria-valid-attr-value` critical.
          Wrap each panel in TabsContent so the trigger/panel link is real
          and aria-controls becomes valid. */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          {canSearchTest && (
            <TabsTrigger value="search" className="gap-1.5"><Search className="h-3.5 w-3.5" /> Search Tester</TabsTrigger>
          )}
          <TabsTrigger value="evaluation" className="gap-1.5"><FlaskConical className="h-3.5 w-3.5" /> Evaluation Runs</TabsTrigger>
        </TabsList>

        {canSearchTest && (
          <TabsContent value="search">
            <SearchTesterSection />
          </TabsContent>
        )}
        <TabsContent value="evaluation">
          <EvaluationSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Search Tester Section ──────────────────────────────────────────────────

function SearchTesterSection() {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [response, setResponse] = useState<SearchTestResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [topK, setTopK] = useState([10]);
  // Result-view is a UI-only filter for which list to render. The backend
  // always runs all three (dense + sparse + fused) — this is a diagnostic
  // endpoint, not a tunable retrieval path.
  const [resultView, setResultView] = useState<'fused' | 'dense' | 'sparse'>('fused');

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const data = await apiClient.post<SearchTestResponse>(
        '/knowledge/search-test',
        { query: trimmed, topK: topK[0] },
      );
      setResponse(data);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Search failed — check that documents are indexed and your role is power_user or higher.';
      toast.error(msg);
      setResponse(null);
    } finally {
      setSearching(false);
    }
  };

  // Pick the active list per UI mode. The "fused" list is always present
  // (empty when both dense and sparse are empty); dense/sparse can be
  // empty when their respective backend (Qdrant or Valkey) is down.
  const activeList: FusedHit[] = response
    ? (resultView === 'dense'
        ? response.dense.map<FusedHit>((d, i) => ({
            chunkId: d.chunkId,
            denseRank: i + 1,
            sparseRank: null,
            fusedScore: d.score,
            text: d.text,
            documentId: d.documentId,
            documentTitle: d.documentTitle,
            source: 'dense',
          }))
        : resultView === 'sparse'
        ? response.sparse.map<FusedHit>((s, i) => ({
            chunkId: s.chunkId,
            denseRank: null,
            sparseRank: i + 1,
            fusedScore: s.score,
            text: s.text,
            documentId: s.documentId,
            documentTitle: s.documentTitle,
            source: 'sparse',
          }))
        : response.fused)
    : [];

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleSearch(); }}
            placeholder="e.g., What is the vibration threshold for pump P-401?"
            className="pl-9"
            maxLength={500}
          />
        </div>
        <Button onClick={() => void handleSearch()} disabled={!query.trim() || searching} className="gap-1.5">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
        </Button>
      </div>

      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground"><SlidersHorizontal className="h-3 w-3" /> Advanced Options</Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-2">
            <CardContent className="pt-4 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Result View</Label>
                <Select value={resultView} onValueChange={v => setResultView(v as typeof resultView)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fused">Fused (RRF — Dense + Keyword)</SelectItem>
                    <SelectItem value="dense">Dense only (Embedding)</SelectItem>
                    <SelectItem value="sparse">Keyword only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Backend always runs all three; this filter only changes which list is shown.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Top K Results: {topK[0]}</Label>
                <Slider value={topK} onValueChange={setTopK} min={1} max={50} step={1} />
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {searching && (
        <div className="flex flex-col items-center py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-sm text-muted-foreground">
            Running dense + keyword + RRF over the tenant document index...
          </p>
        </div>
      )}

      {!searching && hasSearched && response && (
        <div className="space-y-3">
          {/* #680 — Honest empty/low-confidence banner. Renders only
              when the backend classifies the result as below "high"
              and provides a non-empty warning. Color-coded by level
              so a power_user can immediately see "no real match" vs
              "partial match" vs "trustworthy result". */}
          {response.confidence && response.confidence !== 'high' && response.warning && (
            <div
              data-testid="search-confidence-banner"
              data-confidence={response.confidence}
              className={
                response.confidence === 'none'
                  ? 'rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300'
                  : response.confidence === 'low'
                  ? 'rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300'
                  : 'rounded-md border border-muted px-3 py-2 text-sm text-muted-foreground'
              }
            >
              <strong className="capitalize">{response.confidence} confidence:</strong>{' '}
              {response.warning}
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Dense: <span className="font-mono text-foreground/80">{response.dense.length}</span>{' '}
              · Sparse: <span className="font-mono text-foreground/80">{response.sparse.length}</span>{' '}
              · Fused: <span className="font-mono text-foreground/80">{response.fused.length}</span>
            </span>
            <span>{response.durationMs}ms</span>
          </div>

          {activeList.length === 0 && (
            <div className="border border-dashed rounded-lg p-8 text-center">
              <p className="text-sm text-muted-foreground">No results in the {resultView} list.</p>
              <p className="text-[11px] text-muted-foreground/70 mt-1">
                {resultView === 'dense'
                  ? 'Dense search returned 0 hits — check Qdrant connectivity or that documents are indexed.'
                  : resultView === 'sparse'
                  ? 'Keyword search returned 0 hits — no indexed chunk text matches this query, or no document is indexed yet.'
                  : 'No fused chunks — both retrieval paths returned empty.'}
              </p>
            </div>
          )}

          {activeList.map((r, i) => (
            <Card key={r.chunkId} className="transition-colors hover:border-primary/30">
              <CardContent className="pt-4 space-y-2">
                {/* #235 — show document TITLE first so exact-match
                    results are immediately recognisable. The
                    truncated UUID falls to muted secondary text for
                    debugging (chunk lookups, audit trails). The
                    retest mistook a correctly-ranked exact match
                    for an irrelevant Siffron result because the
                    title was buried in the chunk text and the only
                    visible identifier was a UUID prefix. */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[9px] font-mono">#{i + 1}</Badge>
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span
                    className="text-sm font-medium truncate max-w-[20rem]"
                    title={r.documentTitle ?? r.documentId ?? ''}
                  >
                    {r.documentTitle ?? (r.documentId ? `doc:${r.documentId.slice(0, 8)}` : 'unknown-doc')}
                  </span>
                  {r.documentId && (
                    <span className="text-[10px] font-mono text-muted-foreground/70 truncate max-w-[8rem]" title={r.documentId}>
                      {r.documentId.slice(0, 8)}
                    </span>
                  )}
                  <Badge
                    variant="secondary"
                    className={cn(
                      'text-[9px]',
                      r.source === 'both' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
                      r.source === 'dense' && 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
                      r.source === 'sparse' && 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
                    )}
                  >
                    {r.source}
                  </Badge>
                  {r.denseRank != null && (
                    <span className="text-[10px] text-muted-foreground font-mono" title="Dense rank">
                      d#{r.denseRank}
                    </span>
                  )}
                  {r.sparseRank != null && (
                    <span className="text-[10px] text-muted-foreground font-mono" title="Sparse rank">
                      s#{r.sparseRank}
                    </span>
                  )}
                  <div className="flex-1" />
                  <Badge variant="outline" className="text-[9px] font-mono">
                    {resultView === 'fused'
                      ? `RRF ${r.fusedScore.toFixed(4)}`
                      : `score ${r.fusedScore.toFixed(4)}`}
                  </Badge>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 prose prose-sm dark:prose-invert max-w-none">
                  <MarkdownRenderer content={r.text} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!searching && !hasSearched && (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <Sparkles className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">Enter a query to test RAG retrieval</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-md">
            Runs dense (embedding) + keyword (full-text and partial-word) retrieval in parallel, then fuses with Reciprocal Rank Fusion.
            Bypasses the reranker and faithfulness gate — this is a raw diagnostic, not the production answer
            pipeline. Clearance still applies: documents outside your clearance / ACL are filtered out before
            scoring. Requires power_user role.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Evaluation Section ─────────────────────────────────────────────────────

/**
 * W2-G-08: wire to real /admin/evaluation/* endpoints. Wire format
 * matches what the W2-G-07 backend returns from `GET /runs`.
 */
interface EvalRunWire {
  id: string;
  trigger: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  faithfulness: number | null;
  answerRelevancy: number | null;
  contextPrecision: number | null;
  contextRecall: number | null;
  pairCount: number;
  durationMs: number | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
}

// Issue #91 — RAGAS targets aligned with the latestRun summary cards.
// Single source of truth so the per-run history badge, the toast
// summary, and the CSV export agree on what counts as "passing".
const RAGAS_TARGETS = {
  faithfulness: 0.85,
  answerRelevancy: 0.80,
  contextPrecision: 0.75,
  contextRecall: 0.80,
} as const;

function countPassingMetrics(run: EvalRunWire | undefined | null): number {
  if (!run) return 0;
  let passed = 0;
  if ((run.faithfulness ?? 0) >= RAGAS_TARGETS.faithfulness) passed++;
  if ((run.answerRelevancy ?? 0) >= RAGAS_TARGETS.answerRelevancy) passed++;
  if ((run.contextPrecision ?? 0) >= RAGAS_TARGETS.contextPrecision) passed++;
  if ((run.contextRecall ?? 0) >= RAGAS_TARGETS.contextRecall) passed++;
  return passed;
}

/** Exported for the per-role control test (L6-04). */
export function EvaluationSection() {
  // Library LB6-T3 / L6-04 — New Run is POST /admin/evaluation/runs, which
  // asks write:evaluation (admin/evaluation.ts); the run list beside it is
  // read:evaluation. The button is drawn for exactly that key.
  const canStartRun = useHasPermission('write', 'evaluation');
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState<EvalRunWire[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadRuns = async () => {
    try {
      const data = await apiClient.get<{ data: EvalRunWire[]; total: number }>(
        '/admin/evaluation/runs?pageSize=50'
      );
      setRuns(data?.data ?? []);
      setLoadError(null);
    } catch (err) {
      const msg = (err as Error).message ?? 'load failed';
      setLoadError(msg);
    }
  };

  // Initial load + polling while a run is in flight.
  useEffect(() => {
    void loadRuns();
  }, []);

  useEffect(() => {
    if (!activeRunId) return;
    const interval = setInterval(async () => {
      try {
        const run = await apiClient.get<EvalRunWire>(
          `/admin/evaluation/runs/${activeRunId}`
        );
        if (!run) return;
        if (run.status === 'completed' || run.status === 'failed') {
          setRunning(false);
          setActiveRunId(null);
          await loadRuns();
          if (run.status === 'completed') {
            // Issue #91 — `completed` does not imply quality acceptance.
            // Tally how many of the four RAGAS metrics cleared their
            // target so the toast tells the user up front whether this
            // run met the gates.
            const passed = countPassingMetrics(run);
            if (passed === 4) {
              toast.success('Evaluation completed — all 4 metrics met target.');
            } else if (passed === 0) {
              toast.error('Evaluation completed — 0/4 metrics met target. See details below.');
            } else {
              toast.warning(`Evaluation completed — ${passed}/4 metrics met target. See details below.`);
            }
          } else {
            toast.error(`Evaluation failed: ${run.errorMessage ?? 'unknown error'}`);
          }
        }
      } catch (err) {
        // Ignore transient errors during polling.
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [activeRunId]);

  const handleNewRun = async () => {
    setRunning(true);
    toast.info('Evaluation started — polling for completion');
    try {
      const run = await apiClient.post<EvalRunWire>(
        '/admin/evaluation/runs',
        { trigger: 'manual' }
      );
      if (run?.id) {
        setActiveRunId(run.id);
        await loadRuns();
      } else {
        setRunning(false);
        toast.error('Evaluation start failed');
      }
    } catch (err) {
      setRunning(false);
      toast.error(`Evaluation start failed: ${(err as Error).message}`);
    }
  };

  const latestRun = runs[0]; // backend returns newest first

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Runs use the active golden Q&amp;A set ({runs[0]?.pairCount ?? '—'} pairs).
          {/* R2 P2 #3 — fix dead-link banner. The old copy pointed at
              /admin/evaluation/golden which 404s; the actual top-level
              rail is /evaluation (TanStack route created in PR #366). */}
          Manage the dataset via{' '}
          <Link
            to="/evaluation"
            className="text-primary underline-offset-4 hover:underline"
          >
            /evaluation
          </Link>.
        </p>
        {canStartRun && (
          <Button size="sm" className="gap-1.5" onClick={handleNewRun} disabled={running}>
            {running ? <Clock className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {running ? 'Running...' : 'New Run'}
          </Button>
        )}
      </div>

      {loadError && (
        <Card>
          <CardContent className="py-4 text-sm text-amber-600 dark:text-amber-400">
            Could not load evaluation runs: {loadError}
          </CardContent>
        </Card>
      )}

      {latestRun && latestRun.status === 'completed' && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-3 text-xs text-amber-700 dark:text-amber-400">
            <strong>Real RAGAS evaluation output.</strong> Targets shown are aspirational baselines.
            Low scores below target are expected on small or domain-mismatched corpora and reflect
            actual retrieval/generation quality — not a UI bug. Address by tuning chunking, re-ranker,
            or expanding the golden Q&amp;A set, then re-run.
          </CardContent>
        </Card>
      )}

      {latestRun && latestRun.status === 'completed' && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Faithfulness', value: latestRun.faithfulness ?? 0, target: 0.85 },
            { label: 'Answer Relevancy', value: latestRun.answerRelevancy ?? 0, target: 0.80 },
            { label: 'Context Precision', value: latestRun.contextPrecision ?? 0, target: 0.75 },
            { label: 'Context Recall', value: latestRun.contextRecall ?? 0, target: 0.80 },
          ].map(m => (
            <Card key={m.label}>
              <CardContent className="pt-3 pb-2 text-center">
                <p className={cn('text-2xl font-bold', m.value >= m.target ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>
                  {Math.round(m.value * 100)}%
                </p>
                <p className="text-[10px] text-muted-foreground">{m.label}</p>
                <p className="text-[9px] text-muted-foreground/60 mt-0.5">target: {Math.round(m.target * 100)}%</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold mb-3">Run History</h3>
        <div className="rounded-lg border overflow-hidden">
          <div className="grid grid-cols-[1fr_80px_80px_80px_80px_80px_100px_80px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Run</span><span>Pairs</span><span className="text-right">Faith.</span><span className="text-right">Relev.</span><span className="text-right">Prec.</span><span className="text-right">Recall</span><span>Status</span><span>Duration</span>
          </div>
          <div className="divide-y">
            {runs.length === 0 && !loadError && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {canStartRun ? 'No evaluation runs yet. Click "New Run" to trigger one.' : 'No evaluation runs yet.'}
              </div>
            )}
            {runs.map(run => {
              const faithVal = run.faithfulness ?? 0;
              const relevVal = run.answerRelevancy ?? 0;
              const precVal = run.contextPrecision ?? 0;
              const recallVal = run.contextRecall ?? 0;
              return (
              <div key={run.id} className="grid grid-cols-[1fr_80px_80px_80px_80px_80px_100px_80px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors">
                <div>
                  <p className="text-xs font-medium">{run.trigger} run · {run.id.slice(0, 8)}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(run.startedAt).toLocaleString()}</p>
                </div>
                <span className="text-xs font-mono">{run.pairCount}</span>
                <p className={cn('text-xs text-right font-mono', faithVal >= 0.85 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>{run.faithfulness === null ? '—' : `${Math.round(faithVal * 100)}%`}</p>
                <p className={cn('text-xs text-right font-mono', relevVal >= 0.80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>{run.answerRelevancy === null ? '—' : `${Math.round(relevVal * 100)}%`}</p>
                <p className={cn('text-xs text-right font-mono', precVal >= 0.75 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>{run.contextPrecision === null ? '—' : `${Math.round(precVal * 100)}%`}</p>
                <p className={cn('text-xs text-right font-mono', recallVal >= 0.80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>{run.contextRecall === null ? '—' : `${Math.round(recallVal * 100)}%`}</p>
                {(() => {
                  // Issue #91 — status alone doesn't tell the user if a
                  // run cleared the quality gate. A `completed` run
                  // with 1/4 metrics passing rendered as a green
                  // success badge, masking poor retrieval quality.
                  if (run.status === 'completed') {
                    const passed = countPassingMetrics(run);
                    const tone = passed === 4
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : passed === 0
                        ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
                    return (
                      <Badge
                        variant="outline"
                        className={cn('text-[9px] w-fit', tone)}
                        title={passed === 4 ? 'All 4 RAGAS metrics met target' : `${passed} of 4 RAGAS metrics met target — see metric cells for which`}
                      >
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />{passed}/4 pass
                      </Badge>
                    );
                  }
                  if (run.status === 'failed') {
                    return (
                      <Badge variant="outline" className="text-[9px] w-fit bg-red-500/10 text-red-600 dark:text-red-400">
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />failed
                      </Badge>
                    );
                  }
                  return (
                    <Badge variant="outline" className="text-[9px] w-fit bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      <CheckCircle className="h-2.5 w-2.5 mr-0.5" />{run.status}
                    </Badge>
                  );
                })()}
                <span className="text-[10px] text-muted-foreground">{run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : '—'}</span>
              </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
          const rows = runs.map(r => [
            r.id,
            r.trigger,
            r.pairCount,
            r.faithfulness !== null ? Math.round(r.faithfulness * 100) : '',
            r.answerRelevancy !== null ? Math.round(r.answerRelevancy * 100) : '',
            r.contextPrecision !== null ? Math.round(r.contextPrecision * 100) : '',
            r.contextRecall !== null ? Math.round(r.contextRecall * 100) : '',
            r.status,
            r.status === 'completed' ? `${countPassingMetrics(r)}/4` : '',
            r.durationMs ? (r.durationMs / 1000).toFixed(1) : '',
            r.startedAt,
          ]);
          const header = 'RunId,Trigger,Pairs,Faithfulness%,Relevancy%,Precision%,Recall%,Status,GatePass,Duration(s),Started';
          const csv = [header, ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `evaluation-results-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
          URL.revokeObjectURL(url);
          toast.success('Evaluation results exported as CSV');
        }}>
          <Download className="h-3.5 w-3.5" /> Export Results
        </Button>
      </div>
    </div>
  );
}
